/**
 * client/render/animrules.ts — pure third-person anim selection (no three.js). OWNER: RENDER agent.
 *
 * Data: game/public/assets/anims/playeranim_rules_sns.json, the ordered first-match rules of
 * common_mp/mp/playeranim.script filtered to the SnS anim types (research/models-anims.md §1, tools/anim/
 * playeranim.py). This file is the client-side replacement for the engine's BG_AnimScript* evaluation:
 * snapshots carry legsAnim/torsoAnim = 0, so every client derives the anims from RenderPlayer state and
 * events. The mapping from our networked state to the script's condition names is documented in
 * `animContext()` and `eventBlock()` below (ESTIMATEs marked).
 */
import { MoveFlag, PEv, Stance, WeaponId, MeansOfDeath, type WeaponId as WeaponIdT } from '@shared/types';

export interface RuleCondition { in: string[]; not: string[] }
export interface RuleAnim { part: 'both' | 'legs' | 'torso'; anim: string; extra: string[] }
export interface AnimRule { conditions: Record<string, RuleCondition>; anims: RuleAnim[] }
export interface AnimRulesFile { blocks: Record<string, AnimRule[]> }

/** Condition values for one evaluation. Keys are the script's condition names (case-insensitive). */
export type AnimContext = Record<string, string>;

/** playerAnimType / weaponclass per weapon [models-anims.md §1: crossbow:rifle, ballisticknife:pistol,
 * hatchet:grenade, singleknife:melee — from the weapon files]. */
export function weaponAnimType(w: WeaponIdT): { playerAnimType: string; weaponclass: string } {
  switch (w) {
    case WeaponId.KNIFE_BALLISTIC: return { playerAnimType: 'ballisticknife', weaponclass: 'pistol' };
    case WeaponId.HATCHET: return { playerAnimType: 'hatchet', weaponclass: 'grenade' };
    case WeaponId.KNIFE: return { playerAnimType: 'singleknife', weaponclass: 'melee' };
    case WeaponId.CROSSBOW:
    default: return { playerAnimType: 'crossbow', weaponclass: 'rifle' };
  }
}

export const STANCE_NAMES = ['stand', 'crouch', 'prone'] as const;

/** Speed (in/s) below which a player counts as stationary. ESTIMATE (the engine uses movement input). */
export const STATIONARY_SPEED = 20;

/**
 * Movement direction in the player's yaw frame, as the script's `direction` condition. ESTIMATE: quadrants
 * with the forward/backward cones at +-55 deg (the engine uses ps.movementDir 0..7; diagonals map to the
 * side anims except near-straight, which matches how T6 picks strafe-runs while moving diagonally).
 */
export function moveDirection(vx: number, vy: number, yawDeg: number): 'forward' | 'backward' | 'left' | 'right' {
  const y = (yawDeg * Math.PI) / 180;
  const f = vx * Math.cos(y) + vy * Math.sin(y); //  forward component (CoD space)
  const l = -vx * Math.sin(y) + vy * Math.cos(y); // left component
  const ang = (Math.atan2(l, f) * 180) / Math.PI; // 0 forward, +90 left
  const a = Math.abs(ang);
  if (a <= 55) return 'forward';
  if (a >= 125) return 'backward';
  return ang > 0 ? 'left' : 'right';
}

export interface AnimInputs {
  weapon: WeaponIdT;
  stance: number;
  moveFlags: number;
  velocity: ArrayLike<number>;
  yaw: number;
}

/** Build the condition context from a RenderPlayer-like state. */
export function animContext(p: AnimInputs, out: AnimContext = {}): AnimContext {
  const t = weaponAnimType(p.weapon);
  const speed = Math.hypot(p.velocity[0], p.velocity[1]);
  const ads = (p.moveFlags & MoveFlag.ADS) !== 0;
  out.playeranimtype = t.playerAnimType;
  out.weaponclass = t.weaponclass;
  out.stance = STANCE_NAMES[p.stance] ?? 'stand';
  out.weapon_position = ads ? 'ads' : 'hip';
  out.movestatus = speed < STATIONARY_SPEED ? 'stationary' : ads || p.stance === Stance.PRONE ? 'walk' : 'run';
  out.direction = speed < STATIONARY_SPEED ? 'forward' : moveDirection(p.velocity[0], p.velocity[1], p.yaw);
  out.slope = 'level'; // ESTIMATE: slope anims (run_slopeup/down) not driven — needs ground normal
  out.nextplayeranimtype = t.playerAnimType;
  out.nextweaponclass = t.weaponclass;
  return out;
}

/** Which ANIMATIONS/STATE COMBAT block the player is in. */
export function stateBlock(p: AnimInputs): string {
  const speed = Math.hypot(p.velocity[0], p.velocity[1]);
  if (p.moveFlags & MoveFlag.DIVING) return 'ANIMATIONS/STATE COMBAT/dtp_move';
  if (p.moveFlags & MoveFlag.MANTLING) return 'ANIMATIONS/STATE COMBAT/mantle_over_mid'; // ESTIMATE: height not networked
  if (speed < STATIONARY_SPEED) return 'ANIMATIONS/STATE COMBAT/idle';
  if (p.moveFlags & MoveFlag.SPRINTING) return 'ANIMATIONS/STATE COMBAT/sprint';
  // prone crawl rules live only in the walk block; ADS movement uses the walk (shoot-walk) anims
  if (p.stance === Stance.PRONE || p.moveFlags & MoveFlag.ADS) return 'ANIMATIONS/STATE COMBAT/walk';
  return 'ANIMATIONS/STATE COMBAT/run';
}

/**
 * Predictable event -> EVENTS block (+ the weapon whose anim type to use). ESTIMATEs:
 *  - melee: T6 MP melee is always a knife (crossbow melee = knife_mp, ballistic knife = its own blade),
 *    so MELEE_SWING -> EVENTS/knife_melee and MELEE_LUNGE -> EVENTS/knife_melee_charge (not meleeattack,
 *    the gun-butt bash);
 *  - the hatchet throw is an offhand: OFFHAND_START plays raiseweapon (pt_tomahawk_*_pullout) and
 *    OFFHAND_THROW plays fireweapon with the hatchet anim type.
 */
export function eventBlock(ev: number, parm: number, held: WeaponIdT): { block: string; weapon: WeaponIdT } | null {
  const w = (parm & 0xff) as WeaponIdT;
  switch (ev) {
    case PEv.WEAPON_FIRE:
    case PEv.WEAPON_FIRE_LAST: return { block: 'EVENTS/fireweapon', weapon: w || held };
    case PEv.RELOAD_START: return { block: 'EVENTS/reload', weapon: w || held };
    case PEv.WEAPON_RAISE: return { block: (parm >> 8) === 1 ? 'EVENTS/firstraiseweapon' : 'EVENTS/raiseweapon', weapon: w || held };
    case PEv.WEAPON_DROP: return { block: 'EVENTS/dropweapon', weapon: w || held };
    case PEv.MELEE_SWING: return { block: 'EVENTS/knife_melee', weapon: held };
    case PEv.MELEE_LUNGE: return { block: 'EVENTS/knife_melee_charge', weapon: held };
    case PEv.OFFHAND_START: return { block: 'EVENTS/raiseweapon', weapon: WeaponId.HATCHET };
    case PEv.OFFHAND_THROW: return { block: 'EVENTS/fireweapon', weapon: WeaponId.HATCHET };
    case PEv.JUMP: return { block: 'EVENTS/jump', weapon: held };
    case PEv.LAND: return { block: 'EVENTS/land', weapon: held };
    case PEv.DIVE_LAND: return { block: 'DEATH/dtp_land', weapon: held };
    default: return null;
  }
}

/** Stance change -> transition block. */
export function stanceBlock(from: number, to: number): string | null {
  const n = (s: number) => STANCE_NAMES[s] ?? 'stand';
  if (from === to) return null;
  return `EVENTS/${n(from)}_to_${n(to)}`;
}

/** Means of death -> script dmgType [playeranim.script DEATH block]. */
export function dmgTypeForMod(mod: number): string {
  switch (mod) {
    case MeansOfDeath.MELEE: return 'melee';
    case MeansOfDeath.HEAD_SHOT: return 'headshot';
    case MeansOfDeath.EXPLOSIVE:
    case MeansOfDeath.GRENADE:
    case MeansOfDeath.GRENADE_SPLASH:
    case MeansOfDeath.PROJECTILE_SPLASH: return 'explosive';
    default: return 'normal';
  }
}

/**
 * dmgDirection relative to the victim: `dirYaw` is the yaw (deg) of the damage direction (attacker ->
 * victim travel). Hit from the front = the damage travels against the victim's facing. ESTIMATE on the
 * 90 deg quadrant split (engine: BG_GetDamageDirection-style quadrants).
 */
export function dmgDirection(dirYaw: number, victimYaw: number): 'front' | 'back' | 'left' | 'right' {
  // direction the damage CAME FROM, relative to the victim's facing
  let d = (dirYaw + 180 - victimYaw) % 360;
  if (d < 0) d += 360;
  if (d > 180) d -= 360;
  const a = Math.abs(d);
  if (a <= 45) return 'front';
  if (a >= 135) return 'back';
  return d > 0 ? 'left' : 'right';
}

function condMatches(c: RuleCondition, value: string | undefined): boolean {
  if (value === undefined) return false;
  const v = value.toLowerCase();
  if (c.in.length && !c.in.some((x) => x.toLowerCase() === v)) return false;
  if (c.not.length && c.not.some((x) => x.toLowerCase() === v)) return false;
  return true;
}

/** First matching rule of a block, or null. Condition keys are matched case-insensitively. */
export function matchRule(rules: AnimRulesFile, block: string, ctx: AnimContext): AnimRule | null {
  const list = rules.blocks[block];
  if (!list) return null;
  for (const r of list) {
    let ok = true;
    for (const k in r.conditions) {
      if (!condMatches(r.conditions[k], ctx[k.toLowerCase()])) { ok = false; break; }
    }
    if (ok) return r;
  }
  return null;
}

/** Pick one anim of a matched rule; `rand` in [0,1) (the engine picks randomly among a rule's anims). */
export function pickAnim(rule: AnimRule | null, rand: number): RuleAnim | null {
  if (!rule || rule.anims.length === 0) return null;
  return rule.anims[Math.min(rule.anims.length - 1, Math.floor(rand * rule.anims.length))];
}

/** Rule `extra` tokens as a key/value map (e.g. ['duration','400','blendtime','100'] -> {duration:400,...}). */
export function ruleExtras(a: RuleAnim): Record<string, number | true> {
  const out: Record<string, number | true> = {};
  for (let i = 0; i < a.extra.length; i++) {
    const k = a.extra[i].toLowerCase();
    const nx = a.extra[i + 1];
    if (nx !== undefined && /^-?\d+(\.\d+)?$/.test(nx)) { out[k] = Number(nx); i++; } else out[k] = true;
  }
  return out;
}
